// EP-136 T136-022 — the reservation card on the unit-detail page, shown only
// while `unit.status === 'reserved'`. Mutually exclusive with the Book
// action (UnitDetailPage renders one or the other, never both — a unit
// can't be both bookable and already reserved; see UnitDetailPage.tsx).
//
// All fields come straight off `unit.current_booking` (Api::UnitsController
// / Api::PropertiesController#show) — no separate fetch.
import { useState } from 'react'
import { Link } from 'react-router'
import { useTranslation } from 'react-i18next'
import { Card, Badge, Button } from '@/components/ui'
import { useAuth } from '@/hooks/useAuth'
import { useDateFormatter } from '@/hooks/useDateFormatter'
import { formatBaht } from '@/lib/money'
import { CancelBookingModal } from './CancelBookingModal'
import { ExtendBookingModal } from './ExtendBookingModal'
import type { UnitCurrentBooking } from '@/types'

export interface UnitBookingCardProps {
  booking: UnitCurrentBooking
}

// Days remaining until the reservation lapses, ceil-rounded so "today" and
// "tomorrow" both read as at-least-1 rather than 0. The booking-expiry job
// (T136-019) reconciles bookings whose reservation has actually passed, so a
// reading at or below zero here is a narrow race window rather than a
// steady state — it still renders (clamped to 0), it just isn't expected to
// persist.
function daysUntil(isoDate: string): number {
  const diffMs = new Date(isoDate).getTime() - Date.now()
  return Math.max(Math.ceil(diffMs / (24 * 60 * 60 * 1000)), 0)
}

const EXPIRING_SOON_THRESHOLD_DAYS = 3

export function UnitBookingCard({ booking }: UnitBookingCardProps) {
  const { t } = useTranslation('units')
  const { t: tl } = useTranslation('leases')
  const formatDate = useDateFormatter()
  const { user } = useAuth()
  // Cancel/extend are both admin-or-higher server-side (LeasePolicy#
  // cancel_booking? / #extend_reservation_expiry?, deliberately excluding
  // accountant per the standing EP-129 ladder) — same check LeaseDetailPage
  // uses for its own admin+-only actions.
  const isAdminOrOwner = user?.role === 'admin' || user?.role === 'owner'

  const [cancelOpen, setCancelOpen] = useState(false)
  const [extendOpen, setExtendOpen] = useState(false)

  const daysLeft = booking.reservation_expires_at ? daysUntil(booking.reservation_expires_at) : null
  const isExpiringSoon = daysLeft !== null && daysLeft <= EXPIRING_SOON_THRESHOLD_DAYS

  // EP-136 follow-up — both the cancel variant AND the forfeit
  // pre-selection now come straight off the lease's FROZEN snapshot,
  // serialized server-side in `booking_summary` (RentalLease
  // #effective_booking_fee_mode / #effective_booking_fee_forfeit_on_cancel).
  // Previously this re-derived mode from `booking_invoice.item_type` and
  // pre-selected the forfeit radio from a LIVE re-merge of the property's
  // current booking settings — both of which could disagree with what was
  // actually true when the tenant paid, once an operator edited the
  // property's settings afterward. The server now owns that precedence in
  // one place; this component just reads it.
  const billedMode = booking.booking_fee_mode

  return (
    <Card
      className={isExpiringSoon ? 'border-warning bg-warning/5' : undefined}
      data-testid="unit-booking-card"
    >
      <h2 className="text-lg font-semibold mb-4">{t('booking.card_title')}</h2>
      <dl className="space-y-4">
        <div className="min-w-0">
          <dt className="text-sm text-base-content/60">{t('booking.reserved_by')}</dt>
          <dd className="font-medium break-words">
            {booking.customer ? (
              <Link to={`/customers/${booking.customer.id}`} className="text-primary hover:underline">
                {booking.customer.name}
              </Link>
            ) : (
              t('booking.no_customer')
            )}
          </dd>
        </div>
        <div className="min-w-0">
          <dt className="text-sm text-base-content/60">{t('booking.expires_at')}</dt>
          <dd className="font-medium flex items-center gap-2 flex-wrap">
            <span>{booking.reservation_expires_at ? formatDate(booking.reservation_expires_at) : '-'}</span>
            {daysLeft !== null && (
              <Badge variant={isExpiringSoon ? 'warning' : 'ghost'} size="sm">
                {t('booking.days_left', { count: daysLeft })}
              </Badge>
            )}
          </dd>
        </div>
        <div className="min-w-0">
          <dt className="text-sm text-base-content/60">{t('booking.invoice')}</dt>
          <dd className="font-medium flex items-center gap-2 flex-wrap">
            <Link
              to={`/invoices/${booking.booking_invoice.id}`}
              className="text-primary hover:underline"
            >
              {booking.booking_invoice.invoice_number}
            </Link>
            <span>{formatBaht(booking.booking_invoice.amount)}</span>
            <Badge variant={booking.booking_invoice.paid ? 'success' : 'ghost'} size="sm">
              {booking.booking_invoice.paid ? t('booking.invoice_paid') : t('booking.invoice_unpaid')}
            </Badge>
          </dd>
        </div>
      </dl>
      <div className="pt-4 mt-4 border-t border-base-200 flex items-center justify-between flex-wrap gap-3">
        <Link
          to={`/leases/${booking.lease_id}`}
          className="text-primary hover:underline font-medium"
        >
          {t('booking.view_draft_lease')}
        </Link>
        {isAdminOrOwner && (
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={() => setExtendOpen(true)}>
              {tl('booking.extend.action')}
            </Button>
            <Button variant="error" outline size="sm" onClick={() => setCancelOpen(true)}>
              {tl('booking.cancel.action')}
            </Button>
          </div>
        )}
      </div>

      <CancelBookingModal
        isOpen={cancelOpen}
        onClose={() => setCancelOpen(false)}
        leaseId={booking.lease_id}
        invoicePaid={booking.booking_invoice.paid}
        bookingFeeMode={billedMode}
        forfeitOnCancelDefault={booking.booking_fee_forfeit_on_cancel}
      />
      <ExtendBookingModal
        isOpen={extendOpen}
        onClose={() => setExtendOpen(false)}
        leaseId={booking.lease_id}
        currentExpiresAt={booking.reservation_expires_at}
      />
    </Card>
  )
}
